import React, { useState } from "react";
import { Link } from "react-router-dom";
import { useSelector } from "react-redux";
import axios from "axios";
import { useTranslation } from "react-i18next";
import {
  Card,
  Image,
  SegmentedControl,
  Text,
  TextInput,
  Flex,
  ActionIcon,
} from "@mantine/core";
import { IconMap, IconX } from "@tabler/icons-react";
import toast from "react-hot-toast";

import { PaginatedList } from "@components/shared";
import entityColor from "@util/entityColor";

export default function LocationList({
  isIndex,
  items = null,
  requestInfo = {
    url: `/retrieve-locations/`,
    data: {},
  },
  refresh,
}) {
  const { t } = useTranslation();
  const [searchValue, setSearchValue] = useState("");
  const [filters, setFilters] = useState({
    status: "1",
  });
  const [refresh2, setRefresh2] = useState(false);
  const [variety, setVariety] = useState("all");

  const isAdmin = useSelector((state) => state.admin) ? true : false;
  const managerInfo = useSelector((state) => state.manager);

  const statusButtons = [
    { label: t("common:active"), value: "1" },
    { label: t("common:inactive"), value: "2" },
    { label: t("common:deleted"), value: "3" },
  ];

  const varietyButtons = [
    { label: t("common:all"), value: "all", color: "" },
    {
      label: t("mixer:station_singular"),
      value: "1",
      color: entityColor.contest,
    },
    { label: t("mixer:dealer"), value: "2", color: entityColor.campaign },
    { label: t("mixer:retail"), value: "3", color: entityColor.effort },
  ].map((m, i) => ({
    ...m,
    key: i,
  }));

  const reqData = {
    ...filters,
    ...requestInfo.data,
    search_value: searchValue,
    refresh,
    refresh2,
    variety: variety === "all" ? [1, 2, 3] : variety,
  };

  return (
    <div>
      {requestInfo && (
        <React.Fragment>
          <TextInput
            placeholder={t("common:search") + "..."}
            size="lg"
            value={searchValue}
            onChange={(e) => setSearchValue(e.target.value)}
          />
        </React.Fragment>
      )}
      <Flex gap="sm" align="center" mb="sm" mt="sm">
        <SegmentedControl
          value={variety}
          onChange={(e) => setVariety(e)}
          data={varietyButtons}
        />
        <SegmentedControl
          value={filters.status}
          onChange={(e) => setFilters({ ...filters, status: e })}
          data={statusButtons}
        />
      </Flex>
      <PaginatedList
        tableData={items}
        requestInfo={{
          url: requestInfo.url,
          data: reqData,
        }}
        responseMapping={(r) =>
          r.map((m, i) => ({
            ...m,
            key: i,
            link_url: `/locations/${m.id}`,
          }))
        }
        displayFormatter={(item) => (
          <Card key={item.original.id}>
            {item.original.image && (
              <Card.Section>
                <Image
                  src={item.original.image}
                  height={250}
                  alt={`${item.original.title} photo`}
                />
              </Card.Section>
            )}
            <Flex align="center" gap="xs">
              <div style={{ flexGrow: 1 }}>
                <Text fw={700} component={Link} to={item.original.link_url}>
                  {item.original.name}
                </Text>
                {item.original.city && (
                  <Flex align="center" gap="xs">
                    <IconMap size={16} />
                    <Text size="sm">
                      {item.original.city}, {item.original.state_province}
                    </Text>
                  </Flex>
                )}
              </div>
              <div>
                {requestInfo &&
                  requestInfo.data &&
                  !isIndex &&
                  (isAdmin || (managerInfo && managerInfo.organization_id)) && (
                    <div>
                      <RemoveButton
                        reqData={{
                          ...requestInfo.data,
                          location_id: item.original.id,
                          remove: true,
                        }}
                        locationId={item.original.id}
                        onSuccess={() => setRefresh2(!refresh2)}
                      />
                    </div>
                  )}
              </div>
            </Flex>
          </Card>
        )}
      />
    </div>
  );
}

const RemoveButton = ({ locationId, reqData, onSuccess }) => {
  const { t } = useTranslation();
  const [loading, setLoading] = useState(false);

  function onClick() {
    const req = {
      ...reqData,
    };

    setLoading(true);

    axios
      .post(`/locations/${locationId}/associate/`, req)
      .then(() => {
        setLoading(false);
        toast.success(t("common:success") + "!");
        onSuccess();
      })
      .catch((err) => {
        setLoading(false);
        toast.error(err);
      });
  }

  return (
    <ActionIcon
      onClick={onClick}
      size="xs"
      variant="light"
      color="gray"
      loading={loading}
      radius="xl"
      title={t("common:remove")}
    >
      <IconX size={12} />
    </ActionIcon>
  );
};
